import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';

interface League {
  id: string;
  name: string;
  description: string;
  type: 'public' | 'private';
  createdByUserId: string;
  teams?: any[];
  teamsCount?: number;
}

interface LeaguesPageProps {
  onViewRanking: (leagueId: string, leagueName: string) => void;
}

const LeaguesPage: React.FC<LeaguesPageProps> = ({ onViewRanking }) => {
  const [publicLeagues, setPublicLeagues] = useState<League[]>([]);
  const [privateLeagues, setPrivateLeagues] = useState<League[]>([]);
  const [myLeagues, setMyLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'my' | 'public' | 'private'>('my');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newLeague, setNewLeague] = useState({ name: '', description: '', type: 'public', password: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);
  const [leaguePassword, setLeaguePassword] = useState('');

  const fetchLeagues = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [publicRes, privateRes, myRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/leagues/public`, { headers }),
        fetch(`${API_BASE_URL}/api/leagues/private`, { headers }),
        fetch(`${API_BASE_URL}/api/leagues/mine`, { headers }),
      ]);
      const [publicData, privateData, myData] = await Promise.all([
        publicRes.json(), privateRes.json(), myRes.json(),
      ]);
      if (publicData.success) setPublicLeagues(publicData.data);
      if (privateData.success) setPrivateLeagues(privateData.data);
      if (myData.success) setMyLeagues(myData.data);
    } catch (err) {
      setError('Error al cargar las ligas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeagues(); }, []);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const handleCreateLeague = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/leagues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newLeague),
      });
      const data = await res.json();
      if (data.success) { setShowCreateModal(false); fetchLeagues(); }
      else setError(data.message);
    } catch { setError('Error al crear la liga'); }
  };

  const handleJoinLeague = async (leagueId: string, isPrivate: boolean) => {
    if (isPrivate) {
      setSelectedLeagueId(leagueId);
      setLeaguePassword('');
      setShowPasswordModal(true);
    } else {
      await joinLeagueWithPassword(leagueId, '');
    }
  };

  const joinLeagueWithPassword = async (leagueId: string, password: string) => {
    try {
      const token = localStorage.getItem('token');
      const teamId = localStorage.getItem('fantasyTeamId');
      if (!teamId) { setError('Necesitas crear un equipo primero'); return; }
      const res = await fetch(`${API_BASE_URL}/api/leagues/${leagueId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ teamId, password }),
      });
      const data = await res.json();
      if (data.success) { fetchLeagues(); setShowPasswordModal(false); setLeaguePassword(''); }
      else setError(data.message);
    } catch { setError('Error al unirse a la liga'); }
  };

  const filteredLeagues = useMemo(() => {
    const src = activeTab === 'public' ? publicLeagues : activeTab === 'private' ? privateLeagues : myLeagues;
    const filtered = src.filter(l => l.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return filtered.sort((a, b) => {
      if (a.name === 'Clasificación General') return -1;
      if (b.name === 'Clasificación General') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [activeTab, publicLeagues, privateLeagues, myLeagues, searchTerm]);

  const tabs = [
    { key: 'my', label: 'Mis Ligas' },
    { key: 'public', label: 'Globales' },
    { key: 'private', label: 'Privadas' },
  ] as const;

  return (
    <div className="min-h-screen">
      {/* Error toast */}
      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] w-[92vw] max-w-sm">
          <div className="bg-red-500/95 backdrop-blur-sm border border-red-400/40 text-white px-4 py-3 rounded-2xl shadow-xl flex items-center gap-3">
            <span className="text-lg">⚠</span>
            <p className="text-sm font-medium flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-white/60 hover:text-white text-xl leading-none">×</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="px-3 pt-2 pb-3 -mt-4 flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest">FantasyLub</p>
          <h1 className="text-2xl font-black text-white">Ligas</h1>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl font-bold text-sm text-lub-dark"
          style={{ background: '#FFB800' }}
        >
          <span className="text-base leading-none">+</span>
          Crear
        </button>
      </div>

      {/* Search */}
      <div className="px-3 mb-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar liga..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-lub-blue"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.10)' }}
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-3 mb-4">
        <div className="flex rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-all duration-150 ${
                activeTab === tab.key
                  ? 'text-lub-dark rounded-xl mx-0.5 my-0.5'
                  : 'text-white/50 hover:text-white/80'
              }`}
              style={activeTab === tab.key ? { background: '#FFB800' } : {}}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* League list */}
      <div className="px-3">
        {loading ? (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-2 border-lub-blue border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-white/40 text-sm">Cargando ligas...</p>
          </div>
        ) : filteredLeagues.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-4xl mb-3">🏆</p>
            <p className="text-white/60 text-sm font-medium">
              {searchTerm ? `Sin resultados para "${searchTerm}"` : 'No hay ligas aquí todavía'}
            </p>
            {activeTab !== 'my' && (
              <p className="text-white/30 text-xs mt-1">Probá buscar por nombre o crea una</p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {filteredLeagues.map(league => (
              <div
                key={league.id}
                className="rounded-2xl px-4 py-3.5 flex items-center gap-3"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                {/* Icon */}
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
                  style={{ background: 'rgba(0,87,184,0.25)', border: '1px solid rgba(0,87,184,0.4)' }}>
                  {league.type === 'private' ? '🔒' : '🏆'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm leading-tight truncate">{league.name}</p>
                  <p className="text-white/40 text-xs mt-0.5">
                    {league.teamsCount || 0} {(league.teamsCount || 0) === 1 ? 'equipo' : 'equipos'} · {league.type === 'private' ? 'Privada' : 'Pública'}
                  </p>
                </div>

                {/* Action */}
                {activeTab === 'my' ? (
                  <button
                    onClick={() => onViewRanking(league.id, league.name)}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1"
                    style={{ background: 'rgba(0,87,184,0.5)', border: '1px solid rgba(0,87,184,0.6)' }}
                  >
                    Ver
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={() => handleJoinLeague(league.id, league.type === 'private')}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-lub-dark"
                    style={{ background: '#FFB800' }}
                  >
                    Unirse
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create league modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
          <div className="relative w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden"
            style={{ background: '#0D1B2E', border: '1px solid rgba(255,255,255,0.10)' }}>
            <div className="px-5 pt-5 pb-2 flex items-center justify-between border-b border-white/8">
              <h2 className="text-lg font-black text-white">Nueva Liga</h2>
              <button onClick={() => setShowCreateModal(false)} className="w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white"
                style={{ background: 'rgba(255,255,255,0.08)' }}>×</button>
            </div>
            <form onSubmit={handleCreateLeague} className="px-5 py-4 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">Nombre *</label>
                <input
                  type="text"
                  value={newLeague.name}
                  onChange={e => setNewLeague({ ...newLeague, name: e.target.value })}
                  placeholder="Nombre de la liga"
                  required
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm focus:outline-none focus:ring-1 focus:ring-lub-blue"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">Descripción</label>
                <textarea
                  value={newLeague.description}
                  onChange={e => setNewLeague({ ...newLeague, description: e.target.value })}
                  placeholder="Opcional"
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm focus:outline-none focus:ring-1 focus:ring-lub-blue resize-none"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">Tipo</label>
                <div className="flex rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {['public', 'private'].map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setNewLeague({ ...newLeague, type: t })}
                      className={`flex-1 py-2.5 text-xs font-bold transition-all duration-150 ${
                        newLeague.type === t ? 'text-lub-dark rounded-xl mx-0.5 my-0.5' : 'text-white/50'
                      }`}
                      style={newLeague.type === t ? { background: '#FFB800' } : {}}
                    >
                      {t === 'public' ? '🌐 Pública' : '🔒 Privada'}
                    </button>
                  ))}
                </div>
              </div>
              {newLeague.type === 'private' && (
                <div>
                  <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">Contraseña</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newLeague.password}
                      onChange={e => setNewLeague({ ...newLeague, password: e.target.value })}
                      placeholder="Contraseña de acceso"
                      required
                      className="w-full pl-4 pr-10 py-3 rounded-xl text-white placeholder-white/30 text-sm focus:outline-none focus:ring-1 focus:ring-lub-blue"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-1 pb-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-3 rounded-xl text-sm font-bold text-white/60"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 rounded-xl text-sm font-bold text-lub-dark"
                  style={{ background: '#FFB800' }}
                >
                  Crear Liga
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setShowPasswordModal(false); setLeaguePassword(''); }} />
          <div className="relative w-full max-w-sm rounded-t-3xl sm:rounded-3xl overflow-hidden"
            style={{ background: '#0D1B2E', border: '1px solid rgba(255,255,255,0.10)' }}>
            <div className="px-5 pt-5 pb-2 flex items-center justify-between border-b border-white/8">
              <h2 className="text-base font-black text-white">Liga Privada</h2>
              <button onClick={() => { setShowPasswordModal(false); setLeaguePassword(''); }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white"
                style={{ background: 'rgba(255,255,255,0.08)' }}>×</button>
            </div>
            <div className="px-5 py-4 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">Contraseña</label>
                <input
                  type="password"
                  value={leaguePassword}
                  onChange={e => setLeaguePassword(e.target.value)}
                  placeholder="Ingresá la contraseña"
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm focus:outline-none focus:ring-1 focus:ring-lub-blue"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
                />
              </div>
              <div className="flex gap-3 pb-2">
                <button
                  onClick={() => { setShowPasswordModal(false); setLeaguePassword(''); }}
                  className="flex-1 py-3 rounded-xl text-sm font-bold text-white/60"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => selectedLeagueId && joinLeagueWithPassword(selectedLeagueId, leaguePassword)}
                  disabled={!leaguePassword.trim()}
                  className="flex-1 py-3 rounded-xl text-sm font-bold text-lub-dark disabled:opacity-40"
                  style={{ background: '#FFB800' }}
                >
                  Unirse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaguesPage;
